import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { completeOnboardingStepByName } from '../../../lib/services/integration/integrationApi';
import { getNextRegistrationRoute, parseStepNames } from '@constants/userFlow';
import {
  getCurrentPlanFromPaymentStatus,
  effectivePlanStatus,
} from '../../../lib/services/payment/planUtils';

/**
 * Marks the given Onboarding 2.0 step complete for the current user and moves to
 * the next step in the flow (or /home when finished). Centralises the
 * complete-then-navigate wiring so every step page stays consistent.
 *
 * `beforeComplete` runs first (e.g. persist the store URL / category) — if it
 * throws, the step is NOT marked complete and the error surfaces to the caller.
 */
export function useAdvanceOnboardingStep(step: string) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const advance = useCallback(
    async (beforeComplete?: () => Promise<void> | void) => {
      const user = useAuthStore.getState().user;
      if (!user) return;
      setIsSubmitting(true);
      setError(null);
      try {
        if (beforeComplete) await beforeComplete();
        const existing = parseStepNames(user.stepName);
        const updated = await completeOnboardingStepByName(user.id, step, existing);
        // Resolve the next route from the freshly-updated user.
        const fresh = updated ?? useAuthStore.getState().user;
        const hasShopify = fresh?.installations?.some(i => i.platform === 'shopify') ?? false;
        const treatPaymentDone =
          getCurrentPlanFromPaymentStatus(effectivePlanStatus(fresh) ?? '') !== null ||
          !!fresh?.inFreeTrial;
        const next = getNextRegistrationRoute(fresh?.stepName ?? '', hasShopify, treatPaymentDone);
        navigate(next ?? '/home', { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
        setIsSubmitting(false);
      }
    },
    [step, navigate]
  );

  return { advance, isSubmitting, error };
}
